/* UploadImageAction.js */

"use strict";

const _ = require("lodash");
const tus = require("tus-js-client");

const UploadImageDropComponent = require("./UploadImageDropComponent");
const UploadImageProgressComponent = require("./UploadImageProgressComponent");

const {getFileType} = require("./fileConstants");

const FULL_SUCCESSFUL_MSG = "File successfully uploaded.";

const FULL_FAILED_MSG = "File failed to upload.";

const ABORTED_MSG = "File upload was aborted.";

const LOGOUT_CONFIRMATION_MSG =
    "File upload is still in progress. Are you sure you want to sign out? The upload will be canceled.";

const UPLOAD_ENDPOINT = "/gfs/uploads/";

class UploadImageAction {
    constructor(
        activeView,
        userService,
        userSession,
        controlAppNav,
        modalUserFeedback,
        activityMonitor,
        storageFactory,
        logger
    ) {
        this._activeView = activeView;
        this._userService = userService;
        this._userSession = userSession;
        this._controlAppNav = controlAppNav;
        this._modalUserFeedback = modalUserFeedback;
        this._activityMonitor = activityMonitor;
        this._storageFactory = storageFactory;
        this._logger = logger;
        this._odmOsLink = this._activeView._selectedLinkForUploadImage || {};
        this._activeOrgId = _.get(this._userSession, ["activeOrganization", "id"]);
        this._odmOsPlatform = this._activeView._odmOsPlatform;

        this._uploadStartTime = undefined;

        this._metadata = {};
        this._progressComponent = new UploadImageProgressComponent();

        this.shutdown = this.shutdown.bind(this);
        this.run = this.run.bind(this);
        this._logError = this._logError.bind(this);
        this._abortUpload = this._abortUpload.bind(this);

        this._onWindowBeforeunload = this._onWindowBeforeunload.bind(this);
        this._onWindowUnload = this._onWindowUnload.bind(this);

        this._odmOsLinkStatusValues = this._odmOsLink.linkStatusValues;

        this._registerListeners();
    }

    /***********************************/
    /* Start Private Component methods */
    /***********************************/

    _registerListeners() {
        this._userSession.on("OrganizationChange", this.shutdown);
        this._activeView.on("Shutdown", this.shutdown);
        this._userSession.on("BeforeLogout", this.shutdown);
    }

    _registerWindowListeners() {
        window.addEventListener("beforeunload", this._onWindowBeforeunload);
        window.addEventListener("unload", this._onWindowUnload);
    }

    _unregisterListeners() {
        if (this._userSession) {
            this._userSession.removeListener("OrganizationChange", this.shutdown);
            this._userSession.removeListener("BeforeLogout", this.shutdown);
        }

        if (this._activeView) {
            this._activeView.removeListener("Shutdown", this.shutdown);
        }
    }

    _unregisterWindowListeners() {
        window.removeEventListener("beforeunload", this._onWindowBeforeunload);
        window.removeEventListener("unload", this._onWindowUnload);
    }

    _isUploading(status) {
        return [this._odmOsLinkStatusValues.UPLOADING].includes(status);
    }

    _isUploaded(status) {
        return [this._odmOsLinkStatusValues.UPLOADED].includes(status);
    }

    _isComplete(status) {
        return [
            this._odmOsLinkStatusValues.UPLOADED,
            this._odmOsLinkStatusValues.UPLOAD_FAILED,
        ].includes(status);
    }

    /**********************************/
    /* End Private Component methods  */
    /* Start Public Component methods */
    /**********************************/

    run() {
        let progressVeilHidden = false;
        return this._showGetFileVeil()
            .then((files) => {
                if (!files || Object.keys(files).length === 0 || !files[0]) {
                    return Promise.reject({userRejected: true});
                }

                const file = files[0];

                this._metadata = this._getMetadata(file);
                this._progressData = this.getProgressData(file);
                this._uploadingData = this.getUploadingData(file);

                this._progressComponent.setData(this._progressData);

                this._uploadStartTime = Date.now();

                // Show progress veil immediately (don't wait for it)
                this._showProgressVeil().then(() => {
                    progressVeilHidden = true;
                });

                // Start upload and wait for completion
                return this._startUploadQueue(this._uploadingData);
            })
            .then((uploadResult) => {
                // Update plan after successful upload
                return this._updatePlan().then(() => uploadResult);
            })
            .then((uploadResult) => {
                // Show results if upload completed successfully
                if (!progressVeilHidden && uploadResult && uploadResult.uploadComplete) {
                    return this._showResultsVeil();
                }
            })
            .catch((error) => {
                if (error && error.aborted) {
                    this._abortUpload();
                    // Call to showResultsVeil() replaced until a UPLOAD_ABANDONED status is added to the odmOsLink
                    return this._modalUserFeedback.displayNotification(ABORTED_MSG, {
                        title: "Upload Aborted",
                    });
                    // return this._showResultsVeil();
                }

                if (error && !error.userRejected) {
                    this._logError("run", `main run error`)(error);
                    return this._modalUserFeedback.displayError(error);
                }
            });
    }

    async _updatePlan() {
        return Promise.resolve();
    }

    shutdown() {
        this._unregisterListeners();
        _.invoke(this._modalUserFeedback, "hideDisplayed");

        this._activeView = undefined;
        this._userService = undefined;
        this._userSession = undefined;
        this._controlAppNav = undefined;
        this._modalUserFeedback = undefined;
        this._activityMonitor = undefined;
        this._storageFactory = undefined;
        this._progressComponent = undefined;
        this._logger = undefined;
        this._activeOrgId = undefined;
        this._uploadStartTime = undefined;
        this._odmOsPlatform = undefined;
        this._odmOsLinkStatusValues = undefined;
    }

    /********************************/
    /* End Public Component methods */
    /********************************/

    /**
     * Get not completed odmOsLinks abort and delete them.
     * @private
     */
    _abortUpload() {
        _.toArray(this._uploadingData)
            .filter(({odmOsLink}) => !this._isComplete(odmOsLink.linkStatus))
            .forEach(({fileUploader, odmOsLink}, idx) => {
                if (this._isUploading(odmOsLink.linkStatus) || idx === 0) {
                    fileUploader.abort(true);
                }
                return odmOsLink
                    .setLinkStatus(this._odmOsLinkStatusValues.UPLOAD_FAILED)
                    .setErrorMsg("Upload aborted by user")
                    .setUploadFailedOn(Date.now())
                    .update()
                    .catch(
                        this._logError(
                            "_abortUpload",
                            `Failed to update odmOsLink ${odmOsLink.name} to ${this._odmOsLinkStatusValues.UPLOAD_FAILED} status`
                        )
                    );
            });
    }

    /****************************/
    /* Start Show Veils section */
    /****************************/

    _showGetFileVeil() {
        return this._modalUserFeedback.displayCustom(new UploadImageDropComponent(this._logger), {
            dontHide: true,
        });
    }

    _showProgressVeil() {
        return this._modalUserFeedback.displayCustom(this._progressComponent, {
            notDismissable: false,
            dontHide: false,
        });
    }

    _showResultsVeil() {
        const totalUploads = this._uploadingData.length;
        const {successful, failed, aborted} = this._uploadingData.reduce(
            (results, {odmOsLink /*, fileUploader*/}) => {
                results.aborted +=
                    odmOsLink.linkStatus === this._odmOsLinkStatusValues.UPLOAD_FAILED;
                results.successful += this._isUploaded(odmOsLink.linkStatus);
                results.failed +=
                    odmOsLink.linkStatus === this._odmOsLinkStatusValues.UPLOAD_FAILED;
                return results;
            },
            {successful: 0, failed: 0, aborted: 0}
        );

        let title = aborted ? "Upload Aborted" : "Upload Completed";
        let message = "";

        if (successful === totalUploads) {
            message = FULL_SUCCESSFUL_MSG;
        }
        if (failed === totalUploads) {
            title = "Upload Failed";
            message = FULL_FAILED_MSG;
        }

        if (this._modalUserFeedback) {
            if (!successful) {
                return this._modalUserFeedback.displayError({
                    errorMsg: message,
                    errorCode: "",
                    title,
                });
            }
            return this._modalUserFeedback.displayNotification(message, {title});
        }
    }

    /****************************/
    /* End Show Veils section   */
    /****************************/

    _getMetadata(file) {
        const username = _.get(this._userSession, ["user", "userName"]) || "unknown";

        return {
            username: username,
            filename: file.name,
            filetype: "binary",
            destination: "odm",
            organizationId: this._activeOrgId,
            linkId: this._odmOsLink.id,
        };
    }

    getUploadingData(file) {
        return [
            {
                odmOsLink: this._odmOsLink,
                fileUploader: this._createPromisifiedUploader(file, this._odmOsLink),
            },
        ];
    }

    getProgressData(file) {
        return [
            {
                id: this._odmOsLink.id,
                name: file.name,
                fileSize: file.size,
                fileType: getFileType(file.type),
                type: file.type,
            },
        ];
    }

    _createPromisifiedUploader(file, odmOsLink) {
        let uploadResolve;
        let uploadReject;
        let onUploadProgress = () => {};
        let onChunkComplete = () => {};

        const uploadPromise = new Promise((resolve, reject) => {
            uploadResolve = resolve;
            uploadReject = reject;
        });

        let upload = new tus.Upload(file, {
            endpoint: UPLOAD_ENDPOINT,
            retryDelays: [1e2, 8e2, 27e2, 64e2, 125e2],
            metadata: this._metadata,
            chunkSize: 3e6,
            resume: false,
            onError: function (error) {
                uploadReject(error);
            },
            onProgress: function (bytesUploaded, bytesTotal) {
                onUploadProgress(bytesUploaded, bytesTotal);
            },
            onChunkComplete: function (chunkSize, bytesAccepted, bytesTotal) {
                onChunkComplete(chunkSize, bytesAccepted, bytesTotal);
            },
            onSuccess: function () {
                uploadResolve(odmOsLink);
            },
        });

        upload.start = (onProgress, onChunk) => {
            onUploadProgress = onProgress || onUploadProgress;
            onChunkComplete = onChunk || onChunkComplete;
            tus.Upload.prototype.start.call(upload);
            return uploadPromise;
        };
        upload.abort = (...args) => {
            tus.Upload.prototype.abort.apply(upload, args);
            uploadReject({aborted: true});
        };
        return upload;
    }

    _startUploadQueue(data) {
        this._preUpload();

        const timeout = (...args) => new Promise((resolve) => setTimeout(resolve, 1e2, ...args));

        const fileUploadPromise = (fileUploader, odmOsLink, idx) => {
            const onFileUploadProgress_throttled = _.throttle(
                this._onFileUploadProgress.bind(this, idx, odmOsLink),
                1e3,
                {
                    leading: true,
                    trailing: true,
                }
            );

            return odmOsLink
                .setLinkStatus(this._odmOsLinkStatusValues.UPLOADING)
                .update()
                .catch(
                    this._logError(
                        "_startUploadQueue",
                        `Failed to update odmOsLink ${odmOsLink.name} to ${this._odmOsLinkStatusValues.UPLOADING} status`
                    )
                )
                .then(() => fileUploader.start(onFileUploadProgress_throttled))
                .then(() => {
                    onFileUploadProgress_throttled.flush();
                    onFileUploadProgress_throttled.cancel();
                    return this._onFileUploadSuccess(odmOsLink);
                })
                .catch((error) => {
                    onFileUploadProgress_throttled.flush();
                    onFileUploadProgress_throttled.cancel();
                    if (error && error.aborted) {
                        return Promise.reject(error);
                    }
                    return this._onFileUploadError(odmOsLink, error);
                });
        };

        return data
            .reduce((uploadingQueuePromise, {fileUploader, odmOsLink}, idx) => {
                return uploadingQueuePromise
                    .then(timeout)
                    .then(() => fileUploadPromise(fileUploader, odmOsLink, idx));
            }, Promise.resolve())
            .then(() => {
                this._postUpload();
                return {uploadComplete: true};
            })
            .catch((error) => {
                this._postUpload();
                return Promise.reject(error);
            });
    }

    async _updateStatus(odmOsLink, status, method = "updateStatus") {
        return odmOsLink
            .setLinkStatus(status)
            .update()
            .catch(
                this._logError(
                    method,
                    `Failed to update odmOsLink ${odmOsLink.name} to ${status} status`
                )
            );
    }

    _logError(method, desc = "Error") {
        return (error) => this._logger.warn({error}, `${this.constructor.name}.${method}: ${desc}`);
    }

    /*********************************/
    /* Start Upload Handlers         */
    /*********************************/

    _onFileUploadError(odmOsLink, error) {
        this._logError("_onFileUploadError", `Error uploading ${odmOsLink.name}: ${error}`);
        return odmOsLink
            .setLinkStatus(this._odmOsLinkStatusValues.UPLOAD_FAILED)
            .setErrorMsg(error)
            .setUploadFailedOn(Date.now())
            .update()
            .catch(
                this._logError(
                    "_onFileUploadError",
                    `Failed to update odmOsLink ${odmOsLink.name} to ${this._odmOsLinkStatusValues.UPLOAD_FAILED} status`
                )
            );
    }

    _onFileUploadSuccess(odmOsLink) {
        return odmOsLink
            .setLinkStatus(this._odmOsLinkStatusValues.UPLOADED)
            .update()
            .catch(
                this._logError(
                    "_onFileUploadError",
                    `Failed to update odmOsLink ${odmOsLink.name} to ${this._odmOsLinkStatusValues.UPLOADED} status`
                )
            );
    }

    _onFileUploadProgress(idx, odmOsLink, bytesUploaded, bytesTotal) {
        if (this._progressComponent) {
            this._progressComponent.setProgress(idx, bytesUploaded, bytesTotal);
        }

        const progress = {
            lastProgressTime: Date.now(),
            total: bytesTotal,
            downloaded: bytesUploaded,
            percentage: bytesTotal > 0 ? (bytesUploaded / bytesTotal) * 100 : 0,
            speed:
                bytesUploaded > 0
                    ? (bytesUploaded / (Date.now() - this._uploadStartTime)) * 1000
                    : 0,
        };

        odmOsLink
            .setProgress(progress)
            .update({skipUpdatedOn: true, skipAudit: true})
            .catch(
                this._logError(
                    "_onFileUploadProgress",
                    `Failed to update odmOsLink ${odmOsLink.name}`
                )
            );
    }

    /*************************/
    /* End Upload Handlers   */
    /*************************/

    /*********************************/
    /* Start Window Unload Handlers  */
    /*********************************/

    _onWindowBeforeunload(event) {
        const message = "You have pending uploads";
        const uncompleted = this._data.filter(
            ({odmOsLink}) => !this._isComplete(odmOsLink.linkStatus)
        );
        if (uncompleted.length) {
            event.preventDefault();
            event.returnValue = message;
            return message;
        }
    }

    _onWindowUnload() {
        this._abortUpload();
    }

    /*********************************/
    /* End Window Upload Handlers    */
    /*********************************/

    _preUpload() {
        this._activityMonitor.stopPolling();
        this._registerWindowListeners();
        this._controlAppNav.addLogoutPrecheck(LOGOUT_CONFIRMATION_MSG);
        if (this._userSession) {
            this._userSession.on("BeforeLogout", this._abortUpload);
        }
    }

    _postUpload() {
        this._activityMonitor.startPolling();
        this._unregisterWindowListeners();
        this._controlAppNav.removeLogoutPrecheck(LOGOUT_CONFIRMATION_MSG);
        if (this._userSession) {
            this._userSession.removeListener("BeforeLogout", this._abortUpload);
        }
    }
}
module.exports = UploadImageAction;

UploadImageAction.$inject = [
    "gameEditor.activeView",
    "$teve.services.instance.User",
    "$teve.userSession",
    "$teve.controlAppNav",
    "$teve.ui.userFeedback",
    "$teve.activityMonitor",
    "$teve.storageFactory",
    "$teve.logger",
];

UploadImageAction.$displayDetails = {
    type: "Action",
    name: "UploadImageAction",
    icon: "common.upload",
    shown: false,
    title: "Upload Image",
    order: 3,
};
